import { create } from 'zustand';
import apiClient from '../api/client';

export interface Job {
  id: string;
  job_type: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  params?: Record<string, any>;
  result?: Record<string, any>;
  error_message?: string;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
}

interface JobStore {
  jobs: Job[];
  pollInterval: ReturnType<typeof setInterval> | null;
  dismissedIds: Set<string>;

  fetchJobs: () => Promise<void>;
  submitJob: (job_type: string, params: Record<string, any>) => Promise<Job>;
  startPolling: () => void;
  stopPolling: () => void;
  clearCompleted: () => void;
}

export const useJobStore = create<JobStore>((set, get) => ({
  jobs: [],
  pollInterval: null,
  dismissedIds: new Set<string>(),

  fetchJobs: async () => {
    try {
      const resp = await apiClient.get('/v2/jobs?limit=20');
      const allJobs: Job[] = resp.data;
      const prev = get().jobs;
      const dismissed = get().dismissedIds;

      // Detect newly completed jobs for toast notifications
      for (const job of allJobs) {
        if (job.status === 'completed' || job.status === 'failed') {
          const old = prev.find((j) => j.id === job.id);
          if (old && (old.status === 'queued' || old.status === 'running')) {
            // Job just finished — dispatch custom event for toast
            window.dispatchEvent(new CustomEvent('job-finished', { detail: job }));
          }
        }
      }

      // Filter out dismissed jobs
      const jobs = allJobs.filter((j) => !dismissed.has(j.id));
      set({ jobs });

      // Stop polling if no active jobs
      const hasActive = jobs.some((j) => j.status === 'queued' || j.status === 'running');
      if (!hasActive && get().pollInterval) {
        get().stopPolling();
      }
    } catch {
      // ignore auth errors silently
    }
  },

  submitJob: async (job_type: string, params: Record<string, any>) => {
    const resp = await apiClient.post('/v2/jobs/submit', { job_type, params });
    const job: Job = resp.data;
    set((state) => ({ jobs: [job, ...state.jobs] }));
    get().startPolling();
    return job;
  },

  startPolling: () => {
    if (get().pollInterval) return;
    const interval = setInterval(() => get().fetchJobs(), 10000);
    set({ pollInterval: interval });
  },

  stopPolling: () => {
    const interval = get().pollInterval;
    if (interval) {
      clearInterval(interval);
      set({ pollInterval: null });
    }
  },

  clearCompleted: () => {
    // Delete completed/failed jobs on the backend so they don't reappear
    apiClient.delete('/v2/jobs/completed').catch(() => {});
    set((state) => {
      const newDismissed = new Set(state.dismissedIds);
      state.jobs.forEach((j) => {
        if (j.status === 'completed' || j.status === 'failed') {
          newDismissed.add(j.id);
        }
      });
      return {
        jobs: state.jobs.filter((j) => j.status === 'queued' || j.status === 'running'),
        dismissedIds: newDismissed,
      };
    });
  },
}));
